import { gql } from 'apollo-angular';

// Security Types
export const GET_FINANCIAL_SECURITY_TYPES = gql`
  query FinancialSecurityTypes($pageableParam: PageableParamInput!) {
    financialSecurityTypes(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        code
        createdAt
        createdBy
        description
        documentsRequired
        expiryApplicable
        name
        referenceRequired
        supportsAutomatedVerification
        supportsManualVerification
        updatedAt
        updatedBy
        uuid
        verificationMode
      }
    }
  }
`;

export const CREATE_FINANCIAL_SECURITY_TYPE = gql`
  mutation CreateFinancialSecurityType($input: FinancialSecurityTypeDtoInput!) {
    createFinancialSecurityType(input: $input) {
      data {
        active
        code
        createdAt
        createdBy
        description
        documentsRequired
        expiryApplicable
        name
        referenceRequired
        supportsAutomatedVerification
        supportsManualVerification
        updatedAt
        updatedBy
        uuid
        verificationMode
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const UPDATE_FINANCIAL_SECURITY_TYPE = gql`
  mutation UpdateFinancialSecurityType($uuid: UUID!, $input: FinancialSecurityTypeDtoInput!) {
    updateFinancialSecurityType(input: $input, uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        createdAt
        createdBy
        description
        documentsRequired
        expiryApplicable
        name
        referenceRequired
        supportsAutomatedVerification
        supportsManualVerification
        updatedAt
        updatedBy
        uuid
        verificationMode
      }
    }
  }
`;

export const DELETE_FINANCIAL_SECURITY_TYPE = gql`
  mutation DeleteFinancialSecurityType($uuid: UUID!) {
    deleteFinancialSecurityType(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        createdAt
        createdBy
        description
        documentsRequired
        expiryApplicable
        name
        referenceRequired
        supportsAutomatedVerification
        supportsManualVerification
        updatedAt
        updatedBy
        uuid
        verificationMode
      }
    }
  }
`;

// Security Rules
export const GET_FINANCIAL_SECURITY_RULES = gql`
  query FinancialSecurityRules($pageableParam: PageableParamInput!) {
    financialSecurityRules(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        allowedCurrencyCodes
        complianceThresholdPercent
        createdAt
        createdBy
        minValidityDays
        minimumAmount
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const CREATE_FINANCIAL_SECURITY_RULE = gql`
  mutation CreateFinancialSecurityRuleVersion($input: FinancialSecurityRuleDtoInput!) {
    createFinancialSecurityRuleVersion(input: $input) {
      data {
        active
        allowedCurrencyCodes
        complianceThresholdPercent
        createdAt
        createdBy
        minValidityDays
        minimumAmount
        updatedAt
        updatedBy
        uuid
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const UPDATE_FINANCIAL_SECURITY_RULE = gql`
  mutation UpdateFinancialSecurityRule($uuid: UUID!, $input: FinancialSecurityRuleDtoInput!) {
    updateFinancialSecurityRule(input: $input, uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        allowedCurrencyCodes
        complianceThresholdPercent
        createdAt
        createdBy
        minValidityDays
        minimumAmount
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const DELETE_FINANCIAL_SECURITY_RULE = gql`
  mutation DeleteFinancialSecurityRule($uuid: UUID!) {
    deleteFinancialSecurityRule(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        createdAt
        createdBy
        description
        documentsRequired
        expiryApplicable
        name
        referenceRequired
        supportsAutomatedVerification
        supportsManualVerification
        updatedAt
        updatedBy
        uuid
        verificationMode
      }
    }
  }
`;

export const ACTIVATE_FINANCIAL_SECURITY_RULE = gql`
  mutation ActivateFinancialSecurityRule($uuid: UUID!) {
    activateFinancialSecurityRule(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        allowedCurrencyCodes
        complianceThresholdPercent
        createdAt
        createdBy
        minValidityDays
        minimumAmount
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const DEACTIVATE_FINANCIAL_SECURITY_RULE = gql`
  mutation DeactivateFinancialSecurityRule($uuid: UUID!) {
    deactivateFinancialSecurityRule(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        allowedCurrencyCodes
        complianceThresholdPercent
        createdAt
        createdBy
        minValidityDays
        minimumAmount
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

// Banking Configuration
export const GET_BANKING_INTEGRATIONS = gql`
  query BankConfigs($pageableParam: PageableParamInput!) {
    bankConfigs(pageableParam: $pageableParam) {
      content {
        active
        category
        certificateReference
        code
        countryCode
        countryName
        countryUuid
        createdAt
        createdBy
        endpoint
        integrated
        name
        scope
        updatedAt
        updatedBy
        uuid
      }
      totalPages
      totalElements
      size
      number
      numberOfElements
      last
      first
      hasContent
      hasPrevious
      hasNext
    }
  }
`;

export const CREATE_FINANCIAL_SECURITY_BANK = gql`
  mutation CreateBankConfig($input: BankConfigurationDtoInput) {
    createBankConfig(input: $input) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        category
        certificateReference
        code
        countryCode
        countryName
        countryUuid
        createdAt
        createdBy
        endpoint
        integrated
        name
        scope
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const UPDATE_FINANCIAL_SECURITY_BANK = gql`
  mutation UpdateBankConfig($input: BankConfigurationDtoInput, $uuid: UUID) {
    updateBankConfig(input: $input, uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        category
        certificateReference
        code
        countryCode
        countryName
        countryUuid
        createdAt
        createdBy
        endpoint
        integrated
        name
        scope
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const DELETE_FINANCIAL_SECURITY_BANK = gql`
  mutation DeleteBankConfig($uuid: UUID!) {
    deleteBankConfig(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        category
        certificateReference
        code
        countryCode
        countryName
        countryUuid
        createdAt
        createdBy
        endpoint
        integrated
        name
        scope
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

// FOB References
export const GET_FOB_REFERENCES = gql`
  query FobReferenceValues($pageableParam: PageableParamInput!) {
    fobReferenceValues(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        createdAt
        currencyName
        currencyUuid
        effectiveFrom
        effectiveTo
        fobValue
        productName
        unit
        uuid
        product {
          active
          isBps
          minCapacityMt
          productName
          uuid
        }
      }
    }
  }
`;

export const CREATE_FOB_REFERENCE = gql`
  mutation CreateFobReferenceValue($input: FobReferenceValueDtoInput!) {
    createFobReferenceValue(input: $input) {
      data {
        active
        createdAt
        createdBy
        currencyName
        currencyUuid
        effectiveFrom
        effectiveTo
        fobValue
        productName
        productUuid
        unit
        updatedAt
        updatedBy
        uuid
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const UPDATE_FOB_REFERENCE = gql`
  mutation UpdateFobReferenceValue($uuid: UUID!, $input: FobReferenceValueDtoInput!) {
    updateFobReferenceValue(input: $input, uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        createdAt
        createdBy
        currencyName
        currencyUuid
        effectiveFrom
        effectiveTo
        fobValue
        productName
        productUuid
        unit
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const DELETE_FOB_REFERENCE = gql`
  mutation DeleteFobReferenceValue($uuid: UUID!) {
    deleteFobReferenceValue(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        createdAt
        createdBy
        effectiveFrom
        effectiveTo
        fobValue
        product{
          id
          uuid
        }
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

// COVERAGE FORMULAR
export const GET_COVERAGE_FORMULAS = gql`
  query FinancialCoverageFormulas($pageableParam: PageableParamInput!) {
    financialCoverageFormulas(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        aggregationRule
        coverageMonths
        createdAt
        createdBy
        fobMultiplier
        percentageRate
        roundingMode
        roundingScale
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;

export const CREATE_COVERAGE_FORMULA = gql`
  mutation CreateFinancialCoverageFormulaVersion($input: FinancialCoverageFormulaDtoInput!) {
    createFinancialCoverageFormulaVersion(input: $input) {
      data {
        active
        aggregationRule
        coverageMonths
        createdAt
        createdBy
        fobMultiplier
        percentageRate
        roundingMode
        roundingScale
        updatedAt
        updatedBy
        uuid
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const UPDATE_COVERAGE_FORMULA = gql`
  mutation UpdateFinancialCoverageFormula($uuid: UUID!, $input: FinancialCoverageFormulaDtoInput!) {
    updateFinancialCoverageFormula(input: $input, uuid: $uuid) {
      data {
        active
        aggregationRule
        coverageMonths
        createdAt
        createdBy
        fobMultiplier
        percentageRate
        roundingMode
        roundingScale
        updatedAt
        updatedBy
        uuid
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const DELETE_COVERAGE_FORMULA = gql`
  mutation DeleteFinancialCoverageFormula($uuid: UUID!) {
    deleteFinancialCoverageFormula(uuid: $uuid) {
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const ACTIVATE_COVERAGE_FORMULA = gql`
  mutation ActivateFinancialCoverageFormula($uuid: UUID!) {
    activateFinancialCoverageFormula(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        aggregationRule
        coverageMonths
        fobMultiplier
        percentageRate
        roundingMode
        roundingScale
        uuid
      }
    }
  }
`;

// PEGED COST CATEGORIES
export const GET_PEGGED_COST_CATEGORIES = gql`
  query PeggedCostCategories2($pageableParam: PageableParamInput!) {
    peggedCostCategories(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        code
        createdAt
        deductionRule
        description
        name
        updatedAt
        uuid
      }
    }
  }
`;

export const CREATE_PEGGED_COST_CATEGORIES = gql`
  mutation CreatePeggedCostCategory($input: PeggedCostCategoryDtoInput!) {
    createPeggedCostCategory(input: $input) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        deductionRule
        description
        name
        uuid
      }
    }
  }
`;

export const UPDATE_PEGGED_COST_CATEGORIES = gql`
  mutation UpdatePeggedCostCategory($uuid: UUID!, $input: PeggedCostCategoryDtoInput!) {
    updatePeggedCostCategory(input: $input, uuid: $uuid) {
      data {
        active
        code
        deductionRule
        description
        name
        uuid
      }
      message
      status
      fieldsErrors
      extras
    }
  }
`;

export const DELETE_PEGGED_COST_CATEGORIES = gql`
  mutation DeletePeggedCostCategory($uuid: UUID!) {
    deletePeggedCostCategory(uuid: $uuid) {
      message
      status
      fieldsErrors
      extras
    }
  }
`;

//BANK GUARANTEE TYPES
export const CREATE_BANK_GUARANTEE_TYPE = gql`
  mutation CreateBankGuaranteeType($input: BankGuaranteeTypeDtoInput!) {
    createBankGuaranteeType(input: $input) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        name
        uuid
      }
    }
  }
`;

export const UPDATE_BANK_GUARANTEE_TYPE = gql`
  mutation UpdateBankGuaranteeType($uuid: UUID!, $input: BankGuaranteeTypeDtoInput!) {
    updateBankGuaranteeType(input: $input, uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        name
        uuid
      }
    }
  }
`;

export const GET_BANK_GUARANTEE_TYPES = gql`
  query BankGuaranteeTypes($pageableParam: PageableParamInput!) {
    bankGuaranteeTypes(pageableParam: $pageableParam) {
      first
      hasContent
      hasNext
      hasPrevious
      last
      number
      numberOfElements
      size
      totalElements
      totalPages
      content {
        active
        code
        createdAt
        name
        updatedAt
        uuid
      }
      pageable {
        pageNumber
        pageSize
      }
    }
  }
`;

export const DELETE_BANK_GUARANTEE_TYPE = gql`
  mutation DeleteBankGuaranteeType($uuid: UUID!) {
    deleteBankGuaranteeType(uuid: $uuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        code
        createdAt
        createdBy
        name
        updatedAt
        updatedBy
        uuid
      }
    }
  }
`;
